"use client";

import { useId } from "react";

import cn from "classnames";

import { RichText } from "@/components/richText/richText.component";

import { portableTextToPlain } from "@/utils/portableText.util";

import { brown } from "@/app/font";

import type { AdtSocialProofBlock } from "./adtSocialProof.types";
import {
  isAward,
  isQuote,
  rotationSeconds,
  SOCIAL_PROOF_LABELS,
  socialProofLabel,
  toClip,
} from "./adtSocialProof.utils";
import { AdtSocialProofAwards } from "./adtSocialProofAwards.component";
import { AdtSocialProofTestimonials } from "./adtSocialProofTestimonials.component";
import { AdtSocialProofVideo } from "./adtSocialProofVideo.component";

export type { AdtSocialProofBlock } from "./adtSocialProof.types";

export function AdtSocialProof({ block }: { block: AdtSocialProofBlock }) {
  const titleId = useId();
  const titleText = portableTextToPlain(block.title);
  const clips = (block.videos ?? []).flatMap((clip) =>
    clip?._key && clip.video?.asset?.url ? [toClip(clip)] : [],
  );
  const quotes = (block.testimonials ?? []).filter(isQuote);
  const awards = (block.awards ?? []).filter(isAward);
  const showAwards = Boolean(
    awards.length ||
    block.awardsEyebrow ||
    block.awardsLink?.label ||
    block.disclaimer,
  );

  if (!clips.length && !quotes.length && !showAwards) return null;

  const columns = [clips.length, quotes.length, showAwards].filter(
    Boolean,
  ).length;

  return (
    <section
      aria-labelledby={titleText ? titleId : undefined}
      aria-label={titleText ? undefined : "Social proof"}
      data-testid="adt-social-proof"
      className="w-full bg-white py-10 lg:py-14">
      <div className="container mx-auto px-4">
        {titleText ? (
          <h2
            id={titleId}
            className={cn(
              brown.className,
              "text-secondary mx-auto mb-6 max-w-xl text-center text-[28px] leading-[1.15] font-bold text-balance sm:mb-8 sm:max-w-4xl sm:text-[40px] [&_span]:block",
            )}>
            <RichText
              blockComponents={{
                normal: ({ children }) => <span>{children}</span>,
              }}>
              {block.title}
            </RichText>
          </h2>
        ) : null}
        <div
          className={cn(
            "bg-secondary mx-auto grid w-full grid-cols-1 items-stretch overflow-hidden rounded-[24px] text-white lg:min-h-[460px]",
            columns === 3 && "lg:grid-cols-3",
            columns === 2 && "lg:grid-cols-2",
          )}>
          {clips.length ? (
            <AdtSocialProofVideo
              clips={clips}
              stretch={quotes.length > 0 || showAwards}
              playLabel={socialProofLabel(
                block.playVideoLabel,
                SOCIAL_PROOF_LABELS.playVideo,
              )}
              pauseLabel={socialProofLabel(
                block.pauseVideoLabel,
                SOCIAL_PROOF_LABELS.pauseVideo,
              )}
              previousLabel={socialProofLabel(
                block.previousVideoLabel,
                SOCIAL_PROOF_LABELS.previousVideo,
              )}
              nextLabel={socialProofLabel(
                block.nextVideoLabel,
                SOCIAL_PROOF_LABELS.nextVideo,
              )}
              unavailableLabel={socialProofLabel(
                block.videoUnavailableLabel,
                SOCIAL_PROOF_LABELS.videoUnavailable,
              )}
            />
          ) : null}
          {quotes.length ? (
            <AdtSocialProofTestimonials
              quotes={quotes}
              eyebrow={block.testimonialsEyebrow}
              seconds={rotationSeconds(block.testimonialRotationSeconds)}
              previousLabel={socialProofLabel(
                block.previousTestimonialLabel,
                SOCIAL_PROOF_LABELS.previousTestimonial,
              )}
              nextLabel={socialProofLabel(
                block.nextTestimonialLabel,
                SOCIAL_PROOF_LABELS.nextTestimonial,
              )}
            />
          ) : null}
          {showAwards ? (
            <AdtSocialProofAwards
              eyebrow={block.awardsEyebrow}
              awards={awards}
              link={block.awardsLink}
              disclaimer={block.disclaimer}
              showDivider={clips.length > 0 || quotes.length > 0}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}
